/**
 * HDR → SDR tone mapping on the GPU, for Path B.
 *
 * ffmpeg-wasm decodes the HDR source to 10-bit 4:2:0 frames; this module
 * uploads each frame's three planes as 16-bit integer textures and runs one
 * fragment shader that does the whole conversion:
 *
 *   10-bit limited-range Y'CbCr (BT.2020 non-constant luminance)
 *   → R'G'B' → display light in nits (PQ EOTF, or HLG inverse OETF + OOTF)
 *   → BT.2020 → BT.709 primaries
 *   → highlight roll-off: HDR reference white (203 nits, ITU-R BT.2408) lands
 *     at 85% of SDR white, brighter highlights compress smoothly toward 100%
 *   → BT.1886 encoding (gamma 2.4) for an ordinary SDR display.
 *
 * The same chain in ffmpeg's zscale + tonemap filters runs in 32-bit float on
 * the CPU and cost about 15 seconds of work per second of 1080p video in QA;
 * on the GPU it is effectively free, so an HDR clip converts at the speed of
 * its decode.
 *
 * tonemap-math.ts has the same mapping on the CPU for the HDR explainer on
 * Wave's pages; a change to the shader's maths belongs in both.
 */

const VERTEX_SHADER = `#version 300 es
in vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp int;
precision highp usampler2D;
uniform usampler2D uY;
uniform usampler2D uU;
uniform usampler2D uV;
uniform int uTransfer;   // 0 = PQ (SMPTE ST 2084), 1 = HLG (ARIB STD-B67)
uniform int uHeight;
out vec4 outColor;

float pqToNits(float e) {
  const float m1 = 0.1593017578125;
  const float m2 = 78.84375;
  const float c1 = 0.8359375;
  const float c2 = 18.8515625;
  const float c3 = 18.6875;
  float p = pow(max(e, 0.0), 1.0 / m2);
  return 10000.0 * pow(max(p - c1, 0.0) / (c2 - c3 * p), 1.0 / m1);
}

float hlgToScene(float e) {
  const float a = 0.17883277;
  const float b = 0.28466892;
  const float c = 0.55991073;
  return e <= 0.5 ? (e * e) / 3.0 : (exp((e - c) / a) + b) / 12.0;
}

void main() {
  // Framebuffer rows run bottom-up; frame rows run top-down.
  ivec2 p = ivec2(int(gl_FragCoord.x), uHeight - 1 - int(gl_FragCoord.y));
  float y = float(texelFetch(uY, p, 0).r);
  float u = float(texelFetch(uU, p / 2, 0).r);
  float v = float(texelFetch(uV, p / 2, 0).r);

  // 10-bit limited range: luma 64-940, chroma 64-960 centred on 512.
  float Y = (y - 64.0) / 876.0;
  float Cb = (u - 512.0) / 896.0;
  float Cr = (v - 512.0) / 896.0;
  vec3 e = clamp(vec3(
    Y + 1.4746 * Cr,
    Y - 0.16455313 * Cb - 0.57135313 * Cr,
    Y + 1.8814 * Cb
  ), 0.0, 1.0);

  vec3 nits;
  if (uTransfer == 0) {
    nits = vec3(pqToNits(e.r), pqToNits(e.g), pqToNits(e.b));
  } else {
    // HLG is scene-referred: apply the OOTF for a 1000-nit display (system
    // gamma 1.2), which puts the 75% signal level at 203 nits.
    vec3 s = vec3(hlgToScene(e.r), hlgToScene(e.g), hlgToScene(e.b));
    float ys = dot(s, vec3(0.2627, 0.6780, 0.0593));
    nits = 1000.0 * pow(max(ys, 1e-6), 0.2) * s;
  }

  // BT.2020 → BT.709 primaries in linear light (column-major).
  const mat3 toRec709 = mat3(
     1.6605, -0.1246, -0.0182,
    -0.5876,  1.1329, -0.1006,
    -0.0728, -0.0083,  1.1187
  );
  vec3 rgb = max(toRec709 * nits, 0.0) / 203.0;

  // Roll off on the brightest channel so hue survives: linear up to 0.6 of
  // reference white, then an exponential shoulder that approaches SDR white.
  float m = max(max(rgb.r, rgb.g), rgb.b);
  const float knee = 0.6;
  float mapped = m <= knee ? m : knee + (1.0 - knee) * (1.0 - exp(-(m - knee) / (1.0 - knee)));
  rgb *= m > 0.0 ? mapped / m : 0.0;

  outColor = vec4(pow(clamp(rgb, 0.0, 1.0), vec3(1.0 / 2.4)), 1.0);
}
`;

export interface GpuToneMapper {
  /** Tone-map one yuv420p10le frame (little-endian 16-bit samples, planes
   *  back to back) into an SDR RGBA VideoFrame. The caller closes it. */
  frame(data: Uint8Array, timestampUs: number, durationUs: number): VideoFrame;
  close(): void;
}

/** Returns null when the browser has no WebGL2; the caller then falls back to
 *  ffmpeg's CPU tone-mapping filters. */
export function createGpuToneMapper(
  width: number,
  height: number,
  transfer: 'pq' | 'hlg',
): GpuToneMapper | null {
  let canvas: OffscreenCanvas | HTMLCanvasElement;
  if (typeof OffscreenCanvas !== 'undefined') {
    canvas = new OffscreenCanvas(width, height);
  } else {
    canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
  }
  const gl = canvas.getContext('webgl2', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    preserveDrawingBuffer: true,
    premultipliedAlpha: false,
  }) as WebGL2RenderingContext | null;
  if (!gl) return null;

  const compile = (type: number, src: string): WebGLShader | null => {
    const sh = gl.createShader(type);
    if (!sh) return null;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.warn('[wave] tone-map shader failed', gl.getShaderInfoLog(sh));
      return null;
    }
    return sh;
  };
  const vs = compile(gl.VERTEX_SHADER, VERTEX_SHADER);
  const fs = compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = gl.createProgram();
  if (!vs || !fs || !program) return null;
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.warn('[wave] tone-map program failed', gl.getProgramInfoLog(program));
    return null;
  }
  gl.useProgram(program);

  // One triangle pair covering the viewport.
  const quad = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const cw = width >> 1;
  const ch = height >> 1;
  const makePlane = (unit: number, name: string, w: number, h: number): WebGLTexture | null => {
    const tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // Integer textures can't be filtered; texelFetch reads exact samples.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.R16UI, w, h);
    gl.uniform1i(gl.getUniformLocation(program, name), unit);
    return tex;
  };
  const texY = makePlane(0, 'uY', width, height);
  const texU = makePlane(1, 'uU', cw, ch);
  const texV = makePlane(2, 'uV', cw, ch);
  gl.uniform1i(gl.getUniformLocation(program, 'uTransfer'), transfer === 'pq' ? 0 : 1);
  gl.uniform1i(gl.getUniformLocation(program, 'uHeight'), height);
  gl.viewport(0, 0, width, height);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 2);

  const ySamples = width * height;
  const cSamples = cw * ch;
  let aligned: Uint8Array | null = null;

  const upload = (unit: number, tex: WebGLTexture | null, w: number, h: number, plane: Uint16Array): void => {
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, w, h, gl.RED_INTEGER, gl.UNSIGNED_SHORT, plane);
  };

  return {
    frame(data, timestampUs, durationUs) {
      // A Uint16Array view needs a 2-byte-aligned offset; copy if it isn't.
      let src = data;
      if (src.byteOffset % 2) {
        if (!aligned || aligned.byteLength !== src.byteLength) aligned = new Uint8Array(src.byteLength);
        aligned.set(src);
        src = aligned;
      }
      const samples = new Uint16Array(src.buffer, src.byteOffset, ySamples + 2 * cSamples);
      upload(0, texY, width, height, samples.subarray(0, ySamples));
      upload(1, texU, cw, ch, samples.subarray(ySamples, ySamples + cSamples));
      upload(2, texV, cw, ch, samples.subarray(ySamples + cSamples));
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      return new VideoFrame(canvas, { timestamp: timestampUs, duration: durationUs, alpha: 'discard' });
    },
    close() {
      gl.deleteTexture(texY);
      gl.deleteTexture(texU);
      gl.deleteTexture(texV);
      gl.deleteBuffer(quad);
      gl.deleteProgram(program);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    },
  };
}
